import React, { useContext } from 'react';
import PropTypes from 'prop-types';

import './CommunityCommentButton.scss';
import {
    CommunityContext,
    PageContext,
} from '../../../../contexts/ContextCreator';
import CommentIcon from '../../../Icons/CommentIcon/CommentIcon';
import MiniCommentIcon from '../../../Icons/MiniCommentIcon/MiniCommentIcon';

const CommunityCommentButton = ({
    hash,
    expandCommentCallback,
    location,
    redirectToPage,
    onCommentClick,
    isCommunityDiscussionType,
}) => {
    // CONSTS ==========================
    const { isLoggedIn, wrapAuth } = useContext(PageContext);
    const { getContent, openCommentReply, fetchMoreComments } = useContext(
        CommunityContext
    );
    const content = getContent(hash);
    const preloading = content?.preloading;

    if (preloading) content.commentCount = 0;
    const contentIsComment = content?.type === 'community-comment';

    // FUNCTIONS ==========================
    const handleClick = React.useCallback(async () => {
        if (!isLoggedIn()) return;
        if (onCommentClick) {
            onCommentClick();
        }
        if (location === 'contributions') {
            window.location = content.contentUrl;
            return;
        }

        if (contentIsComment && content?.commentCount >= 1) {
            await fetchMoreComments(
                content.parentContentType,
                content.parentContentHash,
                hash,
                null,
                null
            );
            if (expandCommentCallback) {
                expandCommentCallback(true);
            }
        }
        openCommentReply(hash);
    }, [
        hash,
        location,
        openCommentReply,
        fetchMoreComments,
        contentIsComment,
        content.commentCount,
        content.parentContentType,
        content.parentContentHash,
        content.contentUrl,
        onCommentClick,
        expandCommentCallback,
        isLoggedIn,
    ]);

    const renderLabel = () => {
        if (contentIsComment) {
            if (content?.commentCount === 0) return 'Reply';

            return `${content.commentCount} repl${
                content?.commentCount === 1 ? 'y' : 'ies'
            }`;
        }

        if (content.commentCount === 0) return 'Comment';

        return `${content.commentCount} comment${
            content?.commentCount === 1 ? '' : 's'
        }`;
    };

    if (redirectToPage) {
        return (
            <a
                className="component-Community-ReactionBar-CommunityCommentButton"
                href={content?.contentUrl}
            >
                {contentIsComment ? <MiniCommentIcon /> : <CommentIcon />}
                <p
                    className="button-label"
                    {...(isCommunityDiscussionType && {
                        itemProp: 'interactionStatistic',
                        itemScope: 'itemscope',
                        itemType: 'https://schema.org/InteractionCounter',
                    })}
                >
                    {isCommunityDiscussionType && (
                        <>
                            <meta
                                itemProp="interactionType"
                                content="https://schema.org/CommentAction"
                            />
                            <meta
                                itemProp="userInteractionCount"
                                content={content.commentCount}
                            />
                        </>
                    )}
                    {renderLabel()}
                </p>
            </a>
        );
    }

    return wrapAuth(
        `community-like-${content.hash}`,
        <button
            type="button"
            className="component-Community-ReactionBar-CommunityCommentButton"
            onClick={handleClick}
        >
            {contentIsComment ? <MiniCommentIcon /> : <CommentIcon />}
            <p
                className="button-label"
                {...(isCommunityDiscussionType && {
                    itemProp: 'interactionStatistic',
                    itemScope: 'itemscope',
                    itemType: 'https://schema.org/InteractionCounter',
                })}
            >
                {isCommunityDiscussionType && (
                    <>
                        <meta
                            itemProp="interactionType"
                            content="https://schema.org/CommentAction"
                        />
                        <meta
                            itemProp="userInteractionCount"
                            content={content.commentCount}
                        />
                    </>
                )}
                {renderLabel()}
            </p>
        </button>
    );
};

CommunityCommentButton.propTypes = {
    hash: PropTypes.string.isRequired,
    expandCommentCallback: PropTypes.func,
    location: PropTypes.oneOf([
        'masterFeed',
        'miniFeed',
        'groupFeed',
        'singleContent',
        'contributions',
        'homePage',
    ]),
    redirectToPage: PropTypes.bool,
    isCommunityDiscussionType: PropTypes.bool,
};

CommunityCommentButton.defaultProps = {
    expandCommentCallback: null,
    location: 'masterfeed',
    redirectToPage: false,
    isCommunityDiscussionType: false,
};

CommunityCommentButton.displayName = 'CommunityCommentButton';

export default React.memo(CommunityCommentButton);
